// Registration prototype — the two-skin design system, extracted VERBATIM from
// the Claude Design overlays (epic folder: `Hijack Overlay.dc.html`,
// `TCH One Overlay.dc.html`, 2026-08-02). Do not invent values here: when the
// design and this file disagree, the design file wins.
//
// Hijack: saloon-dark — Rye headings, Oswald labels/buttons, Special Elite
// micro-text, lantern-gradient primary on navy #0f1b2f.
// TCH: gallery-light — Bebas Neue headings, Figtree labels/buttons, Playfair
// micro-text, black primary on white; Signature Gold #d2ad74 only in focus
// rings, checked states, avatar ring, and the method-toggle link.

import type { CSSProperties } from 'react';

import avatarAces from './assets/avatar-aces.png';
import avatarBull from './assets/avatar-bull-skull.png';
import avatarLasso from './assets/avatar-lasso.png';
import avatarWhiskey from './assets/avatar-whiskey.png';
import logoHijackBlack from './assets/logo-hijack-black-tight.png';
import logoHijackWhite from './assets/logo-hijack-white-tight.png';
import logoTchBlack from './assets/logo-tch-black.svg';
import logoTchSocialNavy from './assets/logo-tch-social-navy.svg';
import logoTchSocialWhite from './assets/logo-tch-social-white.svg';
import logoTchWhite from './assets/logo-tch-white.svg';

export type D4Skin = 'hijack' | 'tch';

interface TypeStyle {
  fontFamily: string;
  fontWeight?: number;
  fontSize: number;
  letterSpacing?: string;
  textTransform?: 'uppercase' | 'none';
  lineHeight?: number;
  color: string;
}

export interface D4SkinTokens {
  backdrop: string; // story stage behind the scrim
  scrim: string; // design: rgba(3,7,14,.6) + blur(14px), both skins
  cardBg: string; // opaque fallback (no backdrop-filter support)
  /** glassmorphism fill — alpha chosen so worst-case backdrops still clear WCAG AA */
  cardBgGlass: string;
  cardBackdrop: string;
  cardInnerHighlight: string;
  cardBorder: string;
  cardShadow: string;
  heading: TypeStyle;
  body: TypeStyle;
  label: TypeStyle; // field labels (Email / Display name)
  micro: TypeStyle; // footer line + "tap to pick" caption
  /** hairline: dividers, unselected option borders */
  rule: string;
  toggleColor: string; // "Use phone number" inline link
  linkHover: string;
  accent: string; // idShown highlight, paste link, misc
  methodHoverBg: string;
  methodHoverBorder: string;
  pillBg: string; // Luma-style "Paste Code" pill
  pillHoverBg: string;
  primaryBtn: { background: string; color: string; font: TypeStyle; shadow: string };
  methodBtn: { background: string; border: string; font: TypeStyle };
  notNow: TypeStyle;
  input: {
    bg: string;
    border: string;
    color: string;
    fontFamily: string;
    focusBorder: string;
    focusRing: string;
  };
  codeFont: { fontFamily: string; fontWeight?: number; fontSize: number };
  disc: { bg: string; border: string; stroke: string };
  checkbox: { boxBg: string; boxBorder: string; onBg: string; checkStroke: string };
  avatar: { ring: string; glow: string; badgeBg: string; badgeBorder: string };
  tick: { stroke: string; border: string; bg: string; glow: string };
  chromeBtn: { bg: string; border: string; color: string }; // close/back circles
  error: string;
  logos: { src: string; height: number }[];
  logoGap: number;
}

export const AVATARS: string[] = [avatarAces, avatarBull, avatarLasso, avatarWhiskey];

export const D4_TOKENS: Record<D4Skin, D4SkinTokens> = {
  hijack: {
    backdrop: '#0a1322',
    scrim: 'rgba(3,7,14,.6)',
    cardBg: '#0f1b2f',
    cardBgGlass: 'rgba(15,27,47,.78)',
    cardBackdrop: 'blur(28px) saturate(150%)',
    cardInnerHighlight: 'inset 0 1px 0 rgba(255,180,92,.18)',
    cardBorder: '1px solid rgba(232,130,31,.35)',
    cardShadow: '0 24px 70px rgba(0,0,0,.7)',
    // Owner refinement 2026-08-02: decorative fonts (Rye, Special Elite) removed
    // from the overlay system — headings/micro use the functional families.
    heading: {
      fontFamily: "'Inter',sans-serif",
      fontWeight: 700,
      fontSize: 19,
      letterSpacing: '.01em',
      lineHeight: 1.18,
      color: '#f1e3c7'
    },
    body: {
      fontFamily: "'Inter',sans-serif",
      fontSize: 12.5,
      lineHeight: 1.55,
      color: 'rgba(217,199,158,.72)'
    },
    label: {
      fontFamily: "'Oswald',sans-serif",
      fontWeight: 500,
      fontSize: 11,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: '#d9c79e'
    },
    micro: {
      fontFamily: "'Inter',sans-serif",
      fontWeight: 600,
      fontSize: 8.5,
      letterSpacing: '.14em',
      textTransform: 'uppercase',
      color: 'rgba(217,199,158,.72)'
    },
    rule: 'rgba(232,130,31,.25)',
    toggleColor: '#ffb45c',
    linkHover: '#ffd089',
    accent: '#ffb45c',
    methodHoverBg: 'linear-gradient(180deg,rgba(94,45,13,.8),rgba(52,24,8,.9))',
    methodHoverBorder: 'rgba(232,130,31,.55)',
    pillBg: 'rgba(241,227,199,.10)',
    pillHoverBg: 'rgba(241,227,199,.18)',
    primaryBtn: {
      background: 'linear-gradient(180deg,#ffb45c,#e8821f 60%,#b85f12)',
      color: '#1a0e06',
      font: {
        fontFamily: "'Oswald',sans-serif",
        fontWeight: 600,
        fontSize: 14,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        color: '#1a0e06'
      },
      shadow: '0 4px 12px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,220,160,.4)'
    },
    methodBtn: {
      background: 'linear-gradient(180deg,rgba(74,35,10,.55),rgba(40,18,6,.7))',
      border: '1px solid rgba(232,130,31,.3)',
      font: {
        fontFamily: "'Oswald',sans-serif",
        fontWeight: 500,
        fontSize: 13,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: '#f1e3c7'
      }
    },
    notNow: {
      fontFamily: "'Oswald',sans-serif",
      fontWeight: 500,
      fontSize: 13,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: '#d9c79e'
    },
    input: {
      bg: 'rgba(0,0,0,.4)',
      border: '1px solid rgba(232,130,31,.28)',
      color: '#f1e3c7',
      fontFamily: "'Inter',sans-serif",
      focusBorder: '#e8821f',
      focusRing: '0 0 0 3px rgba(232,130,31,.15)'
    },
    codeFont: { fontFamily: "'Oswald',sans-serif", fontWeight: 600, fontSize: 24 },
    disc: {
      bg: 'rgba(232,130,31,.13)',
      border: '1px solid rgba(232,130,31,.4)',
      stroke: '#ffb45c'
    },
    checkbox: {
      boxBg: 'rgba(0,0,0,.3)',
      boxBorder: '1px solid rgba(232,130,31,.28)',
      onBg: '#e8821f',
      checkStroke: '#1a0e06'
    },
    avatar: {
      ring: '#e8821f',
      glow: '0 0 14px rgba(232,130,31,.4)',
      badgeBg: 'linear-gradient(180deg,#ffb45c,#e8821f)',
      badgeBorder: '2px solid #0f1b2f'
    },
    tick: {
      stroke: '#5dba6e',
      border: '#5dba6e',
      bg: 'rgba(93,186,110,.12)',
      glow: '0 0 24px rgba(93,186,110,.3)'
    },
    chromeBtn: { bg: 'rgba(0,0,0,.35)', border: '1px solid rgba(232,130,31,.3)', color: '#d9c79e' },
    error: '#ff7a68',
    logos: [
      { src: logoHijackWhite, height: 29 },
      { src: logoTchWhite, height: 33 },
      { src: logoTchSocialWhite, height: 33 }
    ],
    logoGap: 36
  },
  tch: {
    backdrop: '#e9e5dc',
    scrim: 'rgba(3,7,14,.6)',
    cardBg: '#ffffff',
    cardBgGlass: 'rgba(255,255,255,.85)',
    cardBackdrop: 'blur(28px) saturate(140%)',
    cardInnerHighlight: 'inset 0 1px 0 rgba(255,255,255,.7)',
    cardBorder: '1px solid rgba(0,0,0,.1)',
    cardShadow: '0 24px 70px rgba(0,0,0,.5)',
    heading: {
      fontFamily: "'Bebas Neue',Impact,sans-serif",
      fontWeight: 400,
      fontSize: 26,
      letterSpacing: '.02em',
      textTransform: 'uppercase',
      lineHeight: 0.95,
      color: '#000'
    },
    body: {
      fontFamily: "'Figtree',system-ui,sans-serif",
      fontSize: 12.5,
      lineHeight: 1.55,
      color: 'rgba(0,0,0,.68)'
    },
    label: {
      fontFamily: "'Figtree',system-ui,sans-serif",
      fontWeight: 700,
      fontSize: 10,
      letterSpacing: '.14em',
      textTransform: 'uppercase',
      color: 'rgba(0,0,0,.55)'
    },
    micro: {
      fontFamily: "'Figtree',system-ui,sans-serif",
      fontWeight: 600,
      fontSize: 9,
      letterSpacing: '.24em',
      textTransform: 'uppercase',
      color: 'rgba(0,0,0,.62)'
    },
    // WCAG fix 2026-08-02 (audit): Signature Gold family fails AA as TEXT on
    // white — #d2ad74 is 1.9:1 and even #b08d52 measured only 3.05:1. Text
    // that happens to be gold-toned uses this darkened tone (~5:1 on white);
    // decorative Signature Gold #d2ad74 survives untouched below for rules,
    // focus rings, checked states, and the avatar ring, exactly as the brand
    // honesty note requires.
    rule: 'rgba(0,0,0,.14)',
    toggleColor: '#8a6a35',
    linkHover: '#6f5429',
    accent: '#8a6a35',
    methodHoverBg: '#f5f5f6',
    methodHoverBorder: 'rgba(0,0,0,.3)',
    pillBg: 'rgba(0,0,0,.06)',
    pillHoverBg: 'rgba(0,0,0,.11)',
    primaryBtn: {
      background: '#000',
      color: '#fff',
      font: {
        fontFamily: "'Figtree',system-ui,sans-serif",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: '#fff'
      },
      shadow: '0 2px 8px rgba(0,0,0,.22)'
    },
    methodBtn: {
      background: '#fff',
      border: '1px solid rgba(0,0,0,.16)',
      font: {
        fontFamily: "'Figtree',system-ui,sans-serif",
        fontWeight: 600,
        fontSize: 12.5,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: '#000'
      }
    },
    notNow: {
      fontFamily: "'Figtree',system-ui,sans-serif",
      fontWeight: 600,
      fontSize: 12,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: 'rgba(0,0,0,.6)'
    },
    input: {
      bg: '#fff',
      border: '1px solid rgba(0,0,0,.18)',
      color: '#000',
      fontFamily: "'Figtree',system-ui,sans-serif",
      focusBorder: '#d2ad74',
      focusRing: '0 0 0 3px rgba(210,173,116,.28)'
    },
    codeFont: { fontFamily: "'Bebas Neue',Impact,sans-serif", fontSize: 28 },
    disc: { bg: '#f2f4f8', border: '1px solid rgba(0,0,0,.08)', stroke: '#000000' },
    checkbox: {
      boxBg: '#fff',
      boxBorder: '1px solid rgba(0,0,0,.18)',
      onBg: '#d2ad74',
      checkStroke: '#fff'
    },
    avatar: {
      ring: '#d2ad74',
      glow: '0 0 0 4px rgba(210,173,116,.22)',
      badgeBg: '#000',
      badgeBorder: '2px solid #fff'
    },
    tick: {
      stroke: '#1e8e3e',
      border: '#1e8e3e',
      bg: 'rgba(30,142,62,.1)',
      glow: '0 0 24px rgba(30,142,62,.25)'
    },
    chromeBtn: {
      bg: 'rgba(0,0,0,.05)',
      border: '1px solid rgba(0,0,0,.14)',
      color: 'rgba(0,0,0,.55)'
    },
    error: '#b3261e',
    // TCH-branded flow leads with the parent-brand marks and places Hijack
    // last (owner 2026-08-02), mirroring this skin's welcome copy: "every
    // Texas Card House club and Hijack Poker". The Hijack skin keeps the
    // inverse order for the same reason.
    logos: [
      { src: logoTchBlack, height: 33 },
      { src: logoTchSocialNavy, height: 33 },
      { src: logoHijackBlack, height: 29 }
    ],
    logoGap: 36
  }
};

export function typeStyle(t: TypeStyle): CSSProperties {
  return {
    fontFamily: t.fontFamily,
    fontWeight: t.fontWeight,
    fontSize: t.fontSize,
    letterSpacing: t.letterSpacing,
    textTransform: t.textTransform,
    lineHeight: t.lineHeight,
    color: t.color
  };
}
